import { isNumber } from 'lodash';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type IFontBackground, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import ColorInputGroup from '@/components/color-input-group';
import { SimpleEffectPropsContainer } from '@/components/simple-effect-props-container';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '@/panels/text-layer/hooks';
import { Tracker } from '@/panels/text-layer/utils';
import { type WidgetCommonProps } from '../../../types';

type Props = WidgetCommonProps & {
    onOpenSubPanel: (subPanel: string, type?: string) => void;
};

const FALLBACK_COLOR = '#000000ff';

export const BackgroundInspector: React.FC<Props> = ({ onOpenSubPanel, layerId, recordId, isIntl }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const background = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textBackground = layerData.textBackground || [];

        return textBackground.length > 0 ? textBackground[0] : null;
    }) as IFontBackground | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const hidden = background ? Boolean(background.hidden) : true;
    const color = background ? background.color : FALLBACK_COLOR;
    const roundWeight = background && background.roundWeight ? Math.round(background.roundWeight) : 0;

    const add = () => {
        if (background) return;
        editor.getInstance<TextLayer>('TextLayer').addBackground(layerId);

        Tracker.addBackground({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'shortcut',
        });
    };

    const remove = () => {
        if (!background) return;
        editor.getInstance<TextLayer>('TextLayer').removeEffect(layerId, 'textBackground');
    };

    const setVisibility = (flag: boolean) => {
        if (!background) return;
        editor.getInstance<TextLayer>('TextLayer').setEffectVisibility(layerId, 'textBackground', flag);

        Tracker.toggleBackground({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'shortcut',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                kind: 'textBackground',
                panelPosition: 'shortcut',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!background) return;

        const payload = { idx: 0, data: { color: value } };

        editor.getInstance<TextLayer>('TextLayer').adjustBackgroundProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustBackgroundProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'shortcut',
            });
        }
    };

    const adjustRoundWeight = (value: number | null, options = { writeHistory: true }) => {
        if (!background || !isNumber(value)) return;

        const roundWeight = Math.round(value);
        const payload = { idx: 0, data: { roundWeight } };

        editor.getInstance<TextLayer>('TextLayer').adjustBackgroundProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustBackgroundProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'shortcut',
            });
        }
    };

    return (
        <SimpleEffectPropsContainer
            title={$t((v) => v.textStyleAdd.background)}
            showAdd={background == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
            onJump2SubPanel={() => onOpenSubPanel('textEffect', 'background')}
        >
            {background ? (
                <ColorInputGroup
                    disabled={hidden}
                    width={roundWeight}
                    color={color}
                    showInput={!isIntl}
                    onChange={(val) => adjustRoundWeight(val, { writeHistory: false })}
                    onChangeAfter={(val) => adjustRoundWeight(val, { writeHistory: true })}
                    onAdjustColor={(color) => adjustColor(color, { writeHistory: false })}
                    onAdjustColorAfter={(color, isBrandColor) => {
                        adjustColor(color, { writeHistory: true });
                        if (isBrandColor) {
                            window._stat_track?.('hb_layers_func_use', {
                                func_id: '1_107',
                            });
                        }
                    }}
                    onColorPickerOpenChange={handleColorPickerOpenChange}
                    onToggleBizTab={(bizTab) => {
                        if (bizTab === 'team') {
                            window._stat_track?.('hb_layers_func_click', {
                                func_id: '1_107',
                            });
                        }
                    }}
                />
            ) : null}
        </SimpleEffectPropsContainer>
    );
};
